import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDonor,
  getOrganisation,
  updateOrganisation,
  updateOrganization,
  getBranchPaymentSettings,
  upsertBranchPaymentSettings
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch organisation
export function useOrganisation() {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['organisation', organization?.id],
    queryFn: () => getOrganisation(organization.id),
    enabled: isInitialized && !!organization?.id
  });

  return {
    organisation: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to update organisation
export function useUpdateOrganisation() {
  const queryClient = useQueryClient();
  const { organization, isInitialized } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateOrganisation(id, data);
      // Check if there's an error in the response
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // Wait for query to be invalidated and refetched
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      toast.success('Organization updated successfully');
    },
    onError: error => {
      console.error('Update organisation error:', error);
      toast.error('Failed to update organization');
    }
  });

  return {
    updateOrganisation: mutation.mutate,
    updateOrganisationAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update organization
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ organizationId, data }) => {
      const result = await updateOrganization(organizationId, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast.success('Organization updated successfully');
    },
    onError: error => {
      console.error('Update organization error:', error);
      toast.error('Failed to update organization');
    }
  });

  return {
    updateOrganization: mutation.mutate,
    updateOrganizationAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Stripe publishable key for a DONOR: taken from the donor's own branch, with
// the org-wide key as fallback. This mirrors the edge functions, which always
// pick the secret key from the donor's branch - using the branch currently
// being VIEWED (All Branches, or a country branch with no Stripe row) meant
// the client had no key and cards could not be saved or charged.
export function useDonorStripeKey(donorId) {
  const { isInitialized, user } = useAuthContext();

  const { data, isLoading } = useQuery({
    queryKey: ['donorStripeKey', donorId],
    queryFn: async () => {
      const { data: donor } = await getDonor(donorId);
      if (!donor?.branch_id) return null;
      const { data: settings } = await getBranchPaymentSettings(
        donor.branch_id
      );
      return settings?.stripe_key || null;
    },
    enabled: isInitialized && !!donorId
  });

  return {
    stripeKey: data || user?.organisation?.stripe_key || null,
    isLoading
  };
}

// Hook to fetch branch payment settings
export function useBranchPaymentSettings() {
  const { isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branchPaymentSettings', branchId],
    queryFn: () => getBranchPaymentSettings(branchId),
    enabled: isInitialized && !!branchId
  });

  return {
    paymentSettings: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to update branch payment settings
export function useUpdateBranchPaymentSettings() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async paymentData => {
      const result = await upsertBranchPaymentSettings(branchId, paymentData);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['branchPaymentSettings']
      });
      toast.success('Payment settings updated successfully');
    },
    onError: error => {
      console.error('Update payment settings error:', error);
      toast.error('Failed to update payment settings');
    }
  });

  return {
    updatePaymentSettings: mutation.mutate,
    updatePaymentSettingsAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}
